import ImageIcon from "lucide-react/dist/esm/icons/image";
import Link from "lucide-react/dist/esm/icons/link";
import Upload from "lucide-react/dist/esm/icons/upload";
import { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@quake/shared-ui/components/ui/dialog";
import { Input } from "@quake/shared-ui/components/ui/input";
import { Label } from "@quake/shared-ui/components/ui/label";
import { EditorConfig } from "./editor-config";
import { useCurrentEditor } from "@quake/shared-ui/components/ui/texteditor";
import { Button } from "../button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../tooltip";

export const ImageDialog = ({ config }: { config: EditorConfig }) => {
  const { editor } = useCurrentEditor();
  const [isOpen, setIsOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [altText, setAltText] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!editor) return null;

  const addImageByUrl = () => {
    if (imageUrl.trim()) {
      editor
        .chain()
        .focus()
        .setMarkdownImage({
          src: imageUrl.trim(),
          alt: altText.trim() || undefined,
          alignment: config.imageDefaultAlignment || "left",
        })
        .run();

      setImageUrl("");
      setAltText("");
      setIsOpen(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file");
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 2 * 1024 * 1024) {
      alert("Image size should be less than 2 MB");
      return;
    }

    setIsUploading(true);

    try {
      let imageSrc: string;

      if (config.onImageUpload) {
        // Use custom upload handler
        imageSrc = await config.onImageUpload(file);
      } else {
        // Fallback to base64 encoding
        imageSrc = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      }

      editor
        .chain()
        .focus()
        .setMarkdownImage({
          src: imageSrc,
          alt: new Date().getTime().toString(),
          alignment: config.imageDefaultAlignment || "left",
        })
        .run();

      setAltText("");
      setIsOpen(false);
    } catch (error) {
      console.error("Error uploading image:", error);
      alert("Failed to upload image. Please try again.");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <ToolbarButton onClick={() => setIsOpen(true)} tooltip="Insert Image URL">
          <ImageIcon className="h-4 w-4" />
        </ToolbarButton>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{config.image ? "Insert Image" : "Insert Image URL"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* File Upload — only shown when full image upload is enabled */}
          {config.image && (
            <>
              <div className="space-y-2">
                <Label htmlFor="image-file">Upload Image</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="image-file"
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    disabled={isUploading}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    className="w-full"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {isUploading ? "Uploading..." : "Choose File"}
                  </Button>
                </div>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">Or</span>
                </div>
              </div>
            </>
          )}

          {/* URL Input */}
          <div className="space-y-2">
            <Label htmlFor="image-url">Image URL</Label>
            <Input
              id="image-url"
              type="url"
              placeholder="https://example.com/image.jpg"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addImageByUrl()}
              autoFocus
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button onClick={addImageByUrl} disabled={!imageUrl.trim() || isUploading}>
              <Link className="h-4 w-4 mr-2" />
              Insert Image
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export function ToolbarButton({
  onClick,
  disabled = false,
  isActive = false,
  tooltip,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  isActive?: boolean;
  tooltip?: string;
  children: React.ReactNode;
}) {
  const button = (
    <Button
      type="button"
      variant={isActive ? "default" : "ghost"}
      size="sm"
      onClick={onClick}
      disabled={disabled}
      className="h-8 w-8 p-0 hover:bg-muted hover:text-primary"
    >
      {children}
    </Button>
  );
  if (!tooltip) return button;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent>
          <p>{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
